import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api, { formatAxiosError } from "@/lib/api";
import { absUrl } from "@/lib/config";
import { BACKEND_URL } from "@/lib/config";
import { ArrowRight, Buildings } from "@phosphor-icons/react";

export default function Login() {
  const { user, checked, setUser } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);

  const setCookie = (name, value, days = 7) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    const secure = window.location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/${secure};SameSite=Lax`;
  };

  // Redirect if already logged in
  useEffect(() => {
    if (checked && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [checked, user, navigate]);

  useEffect(() => {
    api.get("/sso/providers/public")
      .then(r => setSsoProviders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSsoProviders([]));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    try {
      let geoPromise = Promise.resolve({ lat: null, lng: null });
      if (navigator.geolocation) {
        geoPromise = new Promise((resolve) => {
          try {
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              () => resolve({ lat: null, lng: null }),
              { timeout: 1500, maximumAge: 300000 }
            );
          } catch {
            resolve({ lat: null, lng: null });
          }
        });
      }
      const geo = await Promise.race([
        geoPromise,
        new Promise((r) => setTimeout(() => r({ lat: null, lng: null }), 1200)),
      ]);
      const { data } = await api.post("/auth/login", {
        email: cleanEmail,
        password: cleanPassword,
        lat: geo.lat,
        lng: geo.lng,
      });

      console.log("token", data.access_token);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      if (data && data.access_token) {
        setCookie("access_token", data.access_token, 7);
        localStorage.setItem("user_token", data.access_token);
      }
      if (data && data.id) {
        setCookie("user_id", String(data.id), 7);
        localStorage.setItem("user_id", String(data.id));
      }

      // Only set user after successful login
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Login failed:", err, err.response?.data);
      setUser(false); // Ensure user is cleared on failed login
      setError(formatAxiosError(err, "Sign-in failed. Please check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = branding.logo_url ? absUrl(branding.logo_url) : "";
  const heroUrl = branding.right_image_url
    ? (branding.right_image_url.startsWith("http") ? branding.right_image_url : absUrl(branding.right_image_url))
    : "";

  return (
    <div className={`min-h-screen grid ${branding.show_right_panel ? "lg:grid-cols-2" : ""}`}>
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center gap-2 rise-in">
          {logoUrl ? (
            <img src={logoUrl} alt={branding.brand_name} className="w-8 h-8 object-cover" data-testid="brand-logo-img" />
          ) : (
            <div
              className="w-8 h-8 flex items-center justify-center"
              style={{ backgroundColor: branding.brand_mark_color }}
              data-testid="brand-logo-fallback"
            >
              <Buildings size={18} weight="bold" color="white" />
            </div>
          )}
          <div>
            <div className="font-display text-lg font-black leading-none" data-testid="brand-name">{branding.brand_name}</div>
            <div className="eyebrow text-[10px] mt-1" data-testid="brand-tagline">{branding.brand_tagline}</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto rise-in rise-in-delay-1">
          <div className="eyebrow mb-4" data-testid="login-eyebrow">{branding.eyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] whitespace-pre-line" data-testid="login-headline">
            {branding.headline}
          </h1>
          <p className="text-sm text-muted-foreground mt-4 max-w-sm" data-testid="login-subcopy">
            {branding.subcopy}
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            {ssoProviders.length > 0 && (
              <div className="space-y-2" data-testid="sso-buttons">
                {ssoProviders.map(p => (
                  <a
                    key={p.key}
                    href={`${BACKEND_URL}/api/sso/${p.key}/start`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-border bg-white text-sm font-semibold hover:bg-muted transition"
                    data-testid={`sso-btn-${p.key}`}
                  >
                    {p.icon ? <span className="text-lg">{p.icon}</span> : null}
                    Sign in with {p.label}
                  </a>
                ))}
                <div className="relative py-2">
                  <div className="absolute inset-x-0 top-1/2 border-t border-border" />
                  <div className="relative inline-block bg-white px-2 text-[10px] font-mono-mini uppercase text-muted-foreground left-1/2 -translate-x-1/2">or</div>
                </div>
              </div>
            )}
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2"
                style={{ boxShadow: "none" }}
                placeholder="name@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="eyebrow">Password</label>
                <Link to="/forgot-password" className="text-[10px] font-mono-mini uppercase tracking-widest text-[#002FA7] hover:underline" data-testid="forgot-password-link">
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-semibold uppercase tracking-wide hover:opacity-90 transition disabled:opacity-50"
              style={{ backgroundColor: branding.brand_mark_color }}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : (branding.submit_label || "Sign in")}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>

        <div className="text-xs text-muted-foreground" data-testid="login-footer">{branding.footer_text}</div>
      </div>

      {/* Right: image */}
      {branding.show_right_panel && (
        <div className="hidden lg:block relative overflow-hidden border-l border-border" data-testid="login-right-panel">
          {heroUrl && (
            <img
              src={heroUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="login-hero-image"
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(45deg, ${branding.right_overlay_color}80 0%, transparent 50%, #00000050 100%)`,
            }}
          />
          <div className="absolute bottom-0 left-0 p-10 text-white">
            <div className="eyebrow text-white/80 mb-2" data-testid="login-right-eyebrow">{branding.right_eyebrow}</div>
            <div className="font-display text-3xl font-black leading-tight max-w-sm" data-testid="login-right-headline">
              {branding.right_headline}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
